<template>
  <nav v-if="total > 0" class="front-pagination" aria-label="分页">
    <button
      type="button"
      class="front-pagination__btn front-pagination__nav"
      :disabled="currentPage <= 1"
      aria-label="上一页"
      @click.stop.prevent="go(currentPage - 1)"
    >
      <span aria-hidden="true">&lt;</span>
    </button>

    <template v-for="(item, idx) in pageItems">
      <span
        v-if="item === '...'"
        :key="'e-' + idx"
        class="front-pagination__btn front-pagination__ellipsis"
        aria-hidden="true"
      >...</span>
      <button
        v-else
        :key="'p-' + item"
        type="button"
        class="front-pagination__btn front-pagination__page"
        :class="{ 'is-active': Number(item) === currentPage }"
        :aria-current="Number(item) === currentPage ? 'page' : null"
        :aria-label="'第 ' + item + ' 页'"
        @click.stop.prevent="go(item)"
      >
        {{ item }}
      </button>
    </template>

    <button
      type="button"
      class="front-pagination__btn front-pagination__nav"
      :disabled="currentPage >= pageCount"
      aria-label="下一页"
      @click.stop.prevent="go(currentPage + 1)"
    >
      <span aria-hidden="true">&gt;</span>
    </button>
  </nav>
</template>

<script>
export default {
  name: 'FrontPagination',
  props: {
    pageNum: { type: Number, default: 1 },
    pageSize: { type: Number, default: 5 },
    total: { type: Number, default: 0 }
  },
  computed: {
    currentPage() {
      const n = Number(this.pageNum)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    pageCount() {
      const size = Number(this.pageSize) > 0 ? Number(this.pageSize) : 5
      const total = Number(this.total)
      const safeTotal = Number.isFinite(total) && total > 0 ? total : 0
      return Math.max(1, Math.ceil(safeTotal / size))
    },
    /** 原型形如: < 1 2 3 ... 8 > */
    pageItems() {
      const count = this.pageCount
      const cur = this.currentPage
      if (count <= 7) {
        return Array.from({ length: count }, (_, i) => i + 1)
      }

      const items = []
      const push = (v) => {
        if (items[items.length - 1] !== v) items.push(v)
      }

      push(1)

      let left = Math.max(2, cur - 1)
      let right = Math.min(count - 1, cur + 1)

      if (cur <= 3) {
        left = 2
        right = 3
      }
      if (cur >= count - 2) {
        left = Math.max(2, count - 2)
        right = count - 1
      }

      if (left > 2) push('...')
      for (let p = left; p <= right; p += 1) push(p)
      if (right < count - 1) push('...')
      push(count)

      return items
    }
  },
  methods: {
    go(page) {
      if (page === '...') return
      const next = Math.min(Math.max(1, Number(page)), this.pageCount)
      if (next !== this.currentPage) {
        this.$emit('change', next)
      }
    }
  }
}
</script>

<style scoped>
/* 配色对齐图一原型分页 */
.front-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 28px;
  margin-bottom: 8px;
  padding: 8px 0 4px;
}

.front-pagination__btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  font-family: inherit;
  user-select: none;
}

button.front-pagination__btn {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

/* 箭头：图二 — 白底浅灰边 + 深色图标 */
.front-pagination__nav:not(:disabled) {
  background: #ffffff;
  border-color: #e8e8e8;
  color: #002b49;
}

button.front-pagination__btn:hover:not(:disabled):not(.is-active) {
  border-color: #002b49;
  color: #002b49;
}

/* 当前页：深蓝底白字 */
.front-pagination__page.is-active {
  border-color: #002b49;
  background: #002b49;
  color: #ffffff;
  cursor: default;
}

/* 不可用箭头：仍白底描边，图标变淡（对齐图二底色，不用灰蓝实底） */
.front-pagination__nav:disabled {
  background: #ffffff;
  border-color: #e8e8e8;
  color: #c0c4cc;
  cursor: not-allowed;
}

/* 省略号：中灰字，仍带浅灰边框方块 */
.front-pagination__ellipsis {
  color: #8a8a8a;
  letter-spacing: 0.5px;
  cursor: default;
}
</style>
